import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Breadcrumbs,
  Link as MuiLink
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import InputAdornment from '@mui/material/InputAdornment';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import TableChartIcon from '@mui/icons-material/TableChart';
import PrintIcon from '@mui/icons-material/Print';
import TablePagination from '@mui/material/TablePagination';

interface Bank {
  id: string;
  shortName: string;
  fullName: string;
  swiftCode: string;
  sarieCode: string;
  address: string;
  relationshipManager: {
    name: string;
    phone: string;
    email: string;
  };
  contactPerson: {
    name: string;
    department: string;
    phone: string;
    email: string;
  };
  status: 'Active' | 'Inactive';
  creationDate: string;
  lastUpdated: string;
  createdBy: string;
  lastUpdatedBy: string;
}

const initialBanks: Bank[] = [
  {
    id: 'B001',
    shortName: 'SNB',
    fullName: 'Saudi National Bank',
    swiftCode: 'SNBKSAJE',
    sarieCode: 'SNB10203',
    address: 'King Fahd Road, Riyadh, Saudi Arabia',
    relationshipManager: {
      name: 'Mohammed Ahmed',
      phone: '+966-11-234-5678',
      email: 'mohammed.a@snb.sa'
    },
    contactPerson: {
      name: 'Abdullah Ali',
      department: 'Corporate Banking',
      phone: '+966-11-234-5679',
      email: 'abdullah.a@snb.sa'
    },
    status: 'Active',
    creationDate: '2024-01-01',
    lastUpdated: '2024-03-15',
    createdBy: 'System Admin',
    lastUpdatedBy: 'System Admin'
  },
  {
    id: 'B002',
    shortName: 'RAJHI',
    fullName: 'Al Rajhi Bank',
    swiftCode: 'RJHISARI',
    sarieCode: 'RAJ10204',
    address: 'Olaya Street, Riyadh, Saudi Arabia',
    relationshipManager: {
      name: 'Fahad Al Rajhi',
      phone: '+966-11-123-4567',
      email: 'fahad.r@alrajhibank.com.sa'
    },
    contactPerson: {
      name: 'Saeed Al Saeed',
      department: 'Corporate Banking',
      phone: '+966-11-123-4568',
      email: 'saeed.s@alrajhibank.com.sa'
    },
    status: 'Active',
    creationDate: '2024-01-10',
    lastUpdated: '2024-03-10',
    createdBy: 'System Admin',
    lastUpdatedBy: 'System Admin'
  },
  {
    id: 'B003',
    shortName: 'RIYAD',
    fullName: 'Riyad Bank',
    swiftCode: 'RYBKKSAJ',
    sarieCode: 'RYD10205',
    address: 'King Abdulaziz Road, Riyadh, Saudi Arabia',
    relationshipManager: {
      name: 'Khalid Al Riyadh',
      phone: '+966-11-321-6547',
      email: 'khalid.r@riyadbank.com.sa'
    },
    contactPerson: {
      name: 'Mansour Al Mansour',
      department: 'Corporate Banking',
      phone: '+966-11-321-6548',
      email: 'mansour.m@riyadbank.com.sa'
    },
    status: 'Inactive',
    creationDate: '2024-01-20',
    lastUpdated: '2024-03-05',
    createdBy: 'System Admin',
    lastUpdatedBy: 'System Admin'
  },
  {
    id: 'B004',
    shortName: 'SABB',
    fullName: 'Saudi British Bank',
    swiftCode: 'SABBSARI',
    sarieCode: 'SABB10206',
    address: 'Prince Sultan Street, Jeddah, Saudi Arabia',
    relationshipManager: {
      name: 'Yousef Al SABB',
      phone: '+966-12-456-7890',
      email: 'yousef.s@sabb.com'
    },
    contactPerson: {
      name: 'Nasser Al Nasser',
      department: 'Corporate Banking',
      phone: '+966-12-456-7891',
      email: 'nasser.n@sabb.com'
    },
    status: 'Active',
    creationDate: '2024-02-01',
    lastUpdated: '2024-03-01',
    createdBy: 'System Admin',
    lastUpdatedBy: 'System Admin'
  },
  {
    id: 'B005',
    shortName: 'ANB',
    fullName: 'Arab National Bank',
    swiftCode: 'ARNBKSAJ',
    sarieCode: 'ANB10207',
    address: 'King Fahd Road, Dammam, Saudi Arabia',
    relationshipManager: {
      name: 'Salem Al Salem',
      phone: '+966-13-987-6543',
      email: 'salem.s@anb.com.sa'
    },
    contactPerson: {
      name: 'Majed Al Majed',
      department: 'Corporate Banking',
      phone: '+966-13-987-6544',
      email: 'majed.m@anb.com.sa'
    },
    status: 'Inactive',
    creationDate: '2024-02-10',
    lastUpdated: '2024-02-28',
    createdBy: 'System Admin',
    lastUpdatedBy: 'System Admin'
  }
];

const BankManagement: React.FC = () => {
  const [banks, setBanks] = useState<Bank[]>(initialBanks);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBank, setEditingBank] = useState<Bank | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [formData, setFormData] = useState<Partial<Bank>>({
    shortName: '',
    fullName: '',
    swiftCode: '',
    sarieCode: '',
    address: '',
    relationshipManager: {
      name: '',
      phone: '',
      email: ''
    },
    contactPerson: {
      name: '',
      department: '',
      phone: '',
      email: ''
    },
    status: 'Active'
  });

  const handleOpenDialog = (bank?: Bank) => {
    if (bank) {
      setEditingBank(bank);
      setFormData(bank);
    } else {
      setEditingBank(null);
      setFormData({
        shortName: '',
        fullName: '',
        swiftCode: '',
        sarieCode: '',
        address: '',
        relationshipManager: {
          name: '',
          phone: '',
          email: ''
        },
        contactPerson: {
          name: '',
          department: '',
          phone: '',
          email: ''
        },
        status: 'Active'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBank(null);
  };

  const handleInputChange = (field: string, value: string) => {
    if (field.includes('.')) {
      const [section, subfield] = field.split('.');
      setFormData(prev => {
        const sectionData = prev[section as keyof typeof prev];
        if (typeof sectionData === 'object' && sectionData !== null) {
          return {
            ...prev,
            [section]: {
              ...sectionData,
              [subfield]: value
            }
          };
        }
        return prev;
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = () => {
    if (editingBank) {
      // Update existing bank
      setBanks(prev => prev.map(bank => 
        bank.id === editingBank.id 
          ? { 
              ...bank, 
              ...formData, 
              lastUpdated: new Date().toISOString().split('T')[0],
              lastUpdatedBy: 'Current User'
            }
          : bank
      ));
    } else {
      // Add new bank
      const newBank: Bank = {
        ...formData as Bank,
        id: `B${String(banks.length + 1).padStart(3, '0')}`,
        creationDate: new Date().toISOString().split('T')[0],
        lastUpdated: new Date().toISOString().split('T')[0],
        createdBy: 'Current User',
        lastUpdatedBy: 'Current User'
      };
      setBanks(prev => [...prev, newBank]);
    }
    handleCloseDialog();
  };

  const handleDelete = (bankId: string) => {
    if (window.confirm('Are you sure you want to delete this bank?')) {
      setBanks(prev => prev.filter(bank => bank.id !== bankId));
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredBanks = banks.filter(bank => 
    bank.shortName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    bank.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    bank.swiftCode.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedBanks = filteredBanks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/dashboard">
          Home
        </MuiLink>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/parameters">
          Parameters
        </MuiLink>
        <Typography color="text.primary">Bank Management</Typography>
      </Breadcrumbs>
      <Typography variant="h4" gutterBottom>
        Bank Management
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<TableChartIcon />}
          sx={{
            borderRadius: 2,
            fontWeight: 500,
            bgcolor: '#fff',
            color: '#1E293B',
            borderColor: '#E5E7EB',
            textTransform: 'none',
            boxShadow: 'none',
            px: 2.5,
            '&:hover': { bgcolor: '#F3F4F6', borderColor: '#6366F1' }
          }}
        >
          Export to Excel
        </Button>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          sx={{
            borderRadius: 2,
            fontWeight: 500,
            bgcolor: '#fff',
            color: '#1E293B',
            borderColor: '#E5E7EB',
            textTransform: 'none',
            boxShadow: 'none',
            px: 2.5,
            '&:hover': { bgcolor: '#F3F4F6', borderColor: '#6366F1' }
          }}
        >
          Print
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          color="primary"
          sx={{
            fontWeight: 600,
            px: 4,
            borderRadius: 2,
            boxShadow: '0 1px 2px 0 rgba(59,130,246,0.08)',
            textTransform: 'none',
            '&:hover': { bgcolor: '#2563EB' }
          }}
        >
          Add New Bank
        </Button>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          alignItems: 'center',
          gap: 2,
          mb: 3,
          p: 2,
          bgcolor: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderRadius: 3,
          boxShadow: '0 1px 3px 0 rgba(0,0,0,0.03)',
        }}
      >
        <TextField
          placeholder="Search banks..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#6B7280' }} />
              </InputAdornment>
            ),
            sx: { bgcolor: '#fff', borderRadius: 2 }
          }}
          sx={{ minWidth: 220, flex: 1 }}
        />
        <FormControl sx={{ minWidth: 180, bgcolor: '#fff', borderRadius: 2 }} size="small">
          <InputLabel>Status</InputLabel>
          <Select
            value={formData.status || ''}
            label="Status"
            onChange={(e) => setFormData({ ...formData, status: e.target.value as 'Active' | 'Inactive' })}
          >
            <MenuItem value="">All Statuses</MenuItem>
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SearchIcon />}
          sx={{ fontWeight: 500, px: 3, borderRadius: 2, boxShadow: 'none', textTransform: 'none' }}
        >
          Search
        </Button>
        <Button
          variant="contained"
          color="warning"
          startIcon={<ClearIcon />}
          onClick={() => { setSearchTerm(''); setFormData({ ...formData, status: undefined }); }}
          sx={{
            fontWeight: 500,
            px: 3,
            borderRadius: 2,
            boxShadow: 'none',
            textTransform: 'none',
          }}
        >
          Reset
        </Button>
      </Box>

      {/* Banks Table */}
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 3, boxShadow: '0 2px 8px 0 rgba(99,102,241,0.06)', background: '#fff', mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Bank ID</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Short Name</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Full Name</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>SWIFT Code</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedBanks.map((bank, idx) => (
              <TableRow
                key={bank.id}
                hover
                sx={{
                  '&:hover': { bgcolor: '#F5F6FF' },
                  borderBottom: idx !== paginatedBanks.length - 1 ? '1px solid #E5E7EB' : 'none',
                  transition: 'background 0.2s',
                }}
              >
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{bank.id}</TableCell>
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{bank.shortName}</TableCell>
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{bank.fullName}</TableCell>
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{bank.swiftCode}</TableCell>
                <TableCell sx={{ py: 1 }}>
                  <Chip 
                    label={bank.status} 
                    size="small"
                    sx={{ 
                      bgcolor: bank.status === 'Active' ? '#D1FAE5' : '#FEE2E2',
                      color: bank.status === 'Active' ? '#059669' : '#DC2626',
                      fontWeight: 500
                    }} 
                  />
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <Stack direction="row" spacing={1}>
                    <IconButton 
                      size="small" 
                      onClick={() => handleOpenDialog(bank)}
                      sx={{ color: '#6366F1' }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleDelete(bank.id)}
                      sx={{ color: '#DC2626' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredBanks.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </TableContainer>

      {/* Add/Edit Bank Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', pb: 1 }}>
          {editingBank ? 'Edit Bank' : 'Add New Bank'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Basic Information</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Short Name"
                    value={formData.shortName}
                    onChange={(e) => handleInputChange('shortName', e.target.value)}
                    required
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    required
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="SWIFT Code"
                    value={formData.swiftCode}
                    onChange={(e) => handleInputChange('swiftCode', e.target.value)}
                    required
                    inputProps={{ maxLength: 11 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="SARIE Code"
                    value={formData.sarieCode}
                    onChange={(e) => handleInputChange('sarieCode', e.target.value)}
                    required
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    required
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Relationship Manager Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Relationship Manager Information</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={formData.relationshipManager?.name}
                    onChange={(e) => handleInputChange('relationshipManager.name', e.target.value)}
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={formData.relationshipManager?.phone}
                    onChange={(e) => handleInputChange('relationshipManager.phone', e.target.value)}
                    inputProps={{ maxLength: 20 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={formData.relationshipManager?.email}
                    onChange={(e) => handleInputChange('relationshipManager.email', e.target.value)}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Contact Person Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Contact Person Information</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={formData.contactPerson?.name}
                    onChange={(e) => handleInputChange('contactPerson.name', e.target.value)}
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Department"
                    value={formData.contactPerson?.department}
                    onChange={(e) => handleInputChange('contactPerson.department', e.target.value)}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={formData.contactPerson?.phone}
                    onChange={(e) => handleInputChange('contactPerson.phone', e.target.value)}
                    inputProps={{ maxLength: 20 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={formData.contactPerson?.email}
                    onChange={(e) => handleInputChange('contactPerson.email', e.target.value)}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Status */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{ 
              color: '#374151',
              '&:hover': { bgcolor: '#F3F4F6' }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              bgcolor: '#6366F1',
              '&:hover': { bgcolor: '#4F46E5' },
              textTransform: 'none',
              px: 3
            }}
          >
            {editingBank ? 'Update Bank' : 'Add Bank'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankManagement; 